/**
 * layerOrder.ts
 * يحدّد موضع إدراج طبقات الطقس في مكدّس MapLibre بحيث تظهر **أسفل** حدود الدول
 * والتسميات (مثل Zoom Earth) بدل تغطيتها — فتبقى الحدود وأسماء المدن واضحة فوق الطبقة.
 */

import type { Map as MaplibreMap } from 'maplibre-gl';

/**
 * خلفية الخريطة لكل طبقة (مطابقة Zoom Earth): لكل طبقة قاعدة خريطة خاصة بها فتظهر
 * ألوانها بأعلى دقّة. الحرارة/الإحساس/الندى/الرطوبة على قاعدة فاتحة (فستقية)،
 * والضغط على قاعدة رمادية فاتحة محايدة (تعبئته اللونية شبه شفافة وخطوطه داكنة)،
 * أما الرياح/الهبّات/الأمطار/الغيوم فعلى قاعدة داكنة لتبرز ألوانها الزاهية.
 */
const LIGHT_BASE_LAYERS = new Set(['temperature', 'feels-like', 'dewpoint', 'humidity', 'pressure']);

/** هل قاعدة خريطة الطبقة الفعّالة داكنة؟ (لا طبقة فعّالة → الثيم العام). */
export function layerBaseIsDark(activeLayer: string | null | undefined, fallbackDark: boolean): boolean {
    if (!activeLayer) return fallbackDark;
    return !LIGHT_BASE_LAYERS.has(activeLayer);
}

import { disableMapbox, imagerySources, imagerySourceLabel, isMapboxAccountFailure } from '../../../config/basemapImagery';

// ─────────────────────────────────────────────────────────────────────────────
// الصور الجوّية تحت الرادار
// ─────────────────────────────────────────────────────────────────────────────

const IMAGERY_PREFIX = 'aerial-imagery';

/**
 * يُظهر أو يُخفي طبقة الصور الجوّية.
 *
 * ── لماذا مع الرادار وحده ────────────────────────────────────────────────────
 * حقول الحرارة والرياح والضغط **كاملة التغطية ومعتمة**، فتحجب ما تحتها تماماً:
 * تحميل صورٍ لن تُرى هدرُ نطاقٍ صرف (وحسابٌ على عدّاد Mapbox). أمّا الرادار فصدىً
 * متفرّق فوق 95% خريطةٍ فارغة — وتلك المساحة الفارغة هي بالضبط ما تملؤه الصور
 * بمعلومة: أين العاصفة بالنسبة للمدينة والطريق والحقل.
 *
 * ── ولماذا في قاع المكدّس ────────────────────────────────────────────────────
 * `beforeId` = أوّل طبقة في النمط، أي **تحت الجميع**: تحت الماء واليابسة والطرق
 * والحدود والتسميات وطبقات الطقس. فتبقى كل خطوط CARTO وتسمياتها فوق الصور كما
 * هي فوق القاعدة اللونية — ولا نحتاج إعادة بناء نمطٍ من الصفر.
 */
/**
 * حارس حصّة Mapbox — يُركَّب مرّة واحدة على الخريطة.
 *
 * ‏MapLibre يُصدر حدث `error` لكل بلاطة تفشل، ويحمل `sourceId` ورمز الحالة. فحين
 * تنفد الحصّة (‏402) أو يبطل المفتاح (‏401) تصل عشرات الأحداث في ثوانٍ — ولو تُركت
 * لظهرت شبكة رمادية مثقوبة ورسائل في الكونسول، وهو ما اتُّفق مع العميل على منعه.
 *
 * فالحارس يلتقط **أوّل** فشلٍ حسابيّ، ويُسقط Mapbox للجلسة كلّها، ويستدعي `onFallback`
 * لتُعاد قاعدة الخريطة الرمادية. والباقي من الأحداث يمرّ على علَمٍ مرفوع فلا يفعل شيئاً.
 *
 * ولا يُزال المستمع: تركيبه مرّة وإبقاؤه أرخص من تتبّع دورة حياته، وهو يخرج فوراً
 * على كل حدثٍ لا يخصّه.
 */
const MAPBOX_GUARD = Symbol.for('skyradar:mapbox-guard');

function armMapboxGuard(map: MaplibreMap, onFallback?: () => void): void {
    const m = map as unknown as Record<symbol, boolean>;
    if (m[MAPBOX_GUARD]) return;
    m[MAPBOX_GUARD] = true;
    // ‏MapLibre 6 يمرّر حدث خطأ مُصنَّفاً (ErrorEvent) لا كائناً حرّاً؛ نقرأ منه الحقلين
    // اللذين يحملهما فعلاً عند فشل مصدر (sourceId + error.status) كما في الإصدار 5.
    map.on('error', (e) => {
        const ev = e as unknown as { sourceId?: string; error?: { status?: number } };
        if (ev?.sourceId !== `${IMAGERY_PREFIX}-mapbox`) return;
        if (!isMapboxAccountFailure(ev.error?.status)) return;   // انقطاع شبكة عابر — يُعيد المتصفّح المحاولة
        if (!disableMapbox()) return;                            // سبق التعطيل
        setAerialImagery(map, false);
        onFallback?.();
    });
}


export function setAerialImagery(map: MaplibreMap, enabled: boolean, onFallback?: () => void): void {
    armMapboxGuard(map, onFallback);
    const sources = imagerySources();
    if (!enabled) {
        for (const src of sources) {
            const id = `${IMAGERY_PREFIX}-${src.id}`;
            try { if (map.getLayer(id)) map.removeLayer(id); } catch { /* تجاهل */ }
            try { if (map.getSource(id)) map.removeSource(id); } catch { /* تجاهل */ }
        }
        return;
    }

    const first = map.getStyle()?.layers?.[0]?.id;
    for (const src of sources) {
        const id = `${IMAGERY_PREFIX}-${src.id}`;
        try {
            if (!map.getSource(id)) {
                map.addSource(id, {
                    type: 'raster',
                    tiles: src.tiles,
                    tileSize: src.tileSize,
                    // السقف المقيس للخدمة. ضبطه صراحةً يجعل MapLibre يُكبّر آخر بلاطة
                    // متاحة بدل أن يطلب بلاطاتٍ ترد 404 — صورةٌ ناعمة بدل فراغ رماديّ.
                    maxzoom: src.maxzoom,
                    attribution: src.attribution,
                } as never);
            }
            if (!map.getLayer(id)) {
                map.addLayer({
                    id,
                    type: 'raster',
                    source: id,
                    paint: {
                        'raster-opacity': 1,
                        // صفر لا افتراضي: التلاشي بين البلاطات يجعل الصور تومض عند كل
                        // تكبير، وهو أوضح ما يكون تحت طبقة رادار ثابتة فوقها.
                        'raster-fade-duration': 0,
                    },
                } as never, first);
            }
        } catch { /* مصدرٌ تعذّر لا يمنع الآخر */ }
    }
    if (import.meta.env.DEV) console.info(`[imagery] ${imagerySourceLabel()}`);
}


/** يطابق أي طبقة خطّية للحدود الإدارية (دول/ولايات/محافظات). */
const BOUNDARY_LINE_RE = /boundary|admin|border/i;


/** هل الحدّ الإداري حدّ دولة (وليس حدّاً داخلياً: ولاية/محافظة)؟ */
function isCountryBoundary(id: string): boolean {
    // في CARTO/OSM: مستوى الدولة admin_level 0/2، والداخلي 4+. أسماء الطبقات الشائعة تحمل
    // "country" أو "_0"/"_2"، أما الداخلية فتحمل "state"/"province"/"_4"/"_6" أو "disputed".
    return /country|admin[_-]?0|admin[_-]?2|_0[_-]|level[_-]?2/i.test(id)
        && !/state|province|region|county|municipal|disputed/i.test(id);
}

/**
 * طبقات الحرارة/الرياح/الرطوبة/الضغط: خطوط الحدود والسواحل فيها سوداء دائماً
 * بلا تغيّر مع الثيم (خلاف بقية الطبقات التي تتبع darkMode).
 */
const FORCE_BLACK_BORDER_LAYERS = new Set([
    'temperature', 'feels-like', 'wet-bulb', 'k-index', 'o-index',
    'wind', 'wind-gusts',
    'humidity', 'dewpoint',
    'pressure',
    // الهطول: قاعدته الجديدة زيتونيّ متوسّط السطوع، فالحدود البيضاء عليها وهج يسحب
    // العين بلا معلومة. الخطّ الداكن يبني الشكل الجغرافي بهدوء ويترك اللون للمطر.
    'precipitation',
    // الرادار المتوقَّع: صدى ملوّن متقطّع كالهطول، فالحدود الداكنة تُبقي الجغرافيا مقروءة.
    'radar-sim',
    // شذوذ 850: الحالة الأشدّ في هذه القائمة. سلّمه متباعد **صفره أبيض ناصع**، فالحدود
    // البيضاء تختفي تماماً حيثما كان الشذوذ قريباً من الصفر — أي في أهدأ مناطق الخريطة
    // وأوسعها. والنتيجة خريطة تتقطّع فيها الجغرافيا: ساحلٌ يظهر فوق البقعة الحمراء ثم
    // ينقطع عند البقعة البيضاء المجاورة. والأسود يبقى مقروءاً على طرفَي السلّم معاً.
    't850-anomaly',
]);

function forcesBlackBorders(activeLayer: string | null | undefined): boolean {
    return !!activeLayer && FORCE_BLACK_BORDER_LAYERS.has(activeLayer);
}

function isBoundaryLine(layer: { type: string; id: string }): boolean {
    return layer.type === 'line' && BOUNDARY_LINE_RE.test(layer.id);
}

/**
 * أي طبقة خطّية تخصّ شبكة النقل (طرق/جسور/أنفاق/سكك/مدارج).
 *
 * التصنيف بالتعبير النمطي على المعرّف لا بقائمة أسماء ثابتة: أسماء طبقات CARTO
 * قد تتغيّر بين إصدارات الستايل، والقائمة الثابتة تفشل صامتةً (لا طرق إطلاقاً)
 * بينما التعبير النمطي يبقى مطابقاً. تُستثنى الحدود صراحةً كي لا يبتلعها التصنيف.
 */
const ROAD_LINE_RE = /road|street|highway|motorway|trunk|transportation|bridge|tunnel|rail|runway|taxiway|aeroway|pier|path|ferry/i;

/**
 * رتبة الطريق تحدّد ثخانته وشفافيته — لا متى يظهر.
 *
 * **لا حدّ زووم على أي رتبة** (بطلب صريح): كل طبقة طرق مداها 0→24، فتُرسم في كل
 * مستويات الخريطة كما في Windy و Zoom Earth. الرتبة تبقى لأن الشريان السريع يجب أن
 * يُقرأ أثخن من زقاق، لا لأنه يظهر قبله.
 */
type RoadTier = 'major' | 'mid' | 'minor' | 'rail' | 'faint' | 'hidden';

function roadTier(id: string): RoadTier {
    if (/ferry/i.test(id)) return 'hidden';                       // خطوط العبّارات فوق البحر — ليست طريقاً مرئياً في Windy
    if (/path|footway|pedestrian|track|steps|pier/i.test(id)) return 'faint';
    if (/rail|runway|taxiway|aeroway/i.test(id)) return 'rail';
    if (/motorway|trunk|highway|_major|major_/i.test(id)) return 'major';
    if (/primary|secondary|tertiary/i.test(id)) return 'mid';
    return 'minor';
}

/**
 * ثخانة وشفافية كل رتبة، بدلالة الزووم — **بلا أدنى زووم**: كل الرتب مداها 0→24.
 *
 * منحنيات الشفافية تبدأ من أول مستوى فيه بيانات طرق أصلاً، ولا تبدأ من صفر: الخطّ
 * الذي شفافيته 0.05 لا يُرى، وطلبُك وضوحٌ مثل Windy لا مجرّد وجود. القِيَم مضبوطة
 * ليبقى الطريق مقروءاً فوق حقل طقس مشبع دون أن ينافس حدود الدول.
 */
const ROAD_STYLE: Record<Exclude<RoadTier, 'hidden'>, {
    width: unknown; opacity: unknown;
}> = {
    major: {
        width: ['interpolate', ['linear'], ['zoom'], 2, 0.3, 5, 0.5, 8, 1.0, 11, 1.9, 14, 3.2, 18, 6],
        opacity: ['interpolate', ['linear'], ['zoom'], 2, 0.34, 5, 0.46, 8, 0.58, 11, 0.66],
    },
    mid: {
        width: ['interpolate', ['linear'], ['zoom'], 2, 0.25, 6, 0.45, 10, 1.0, 14, 2.2, 18, 4.5],
        opacity: ['interpolate', ['linear'], ['zoom'], 2, 0.28, 6, 0.38, 10, 0.50, 13, 0.58],
    },
    minor: {
        width: ['interpolate', ['linear'], ['zoom'], 2, 0.22, 8, 0.4, 12, 0.9, 16, 2.2],
        opacity: ['interpolate', ['linear'], ['zoom'], 2, 0.24, 8, 0.32, 12, 0.44, 15, 0.52],
    },
    rail: {
        width: ['interpolate', ['linear'], ['zoom'], 2, 0.22, 8, 0.4, 12, 0.8, 16, 1.6],
        opacity: ['interpolate', ['linear'], ['zoom'], 2, 0.20, 8, 0.28, 12, 0.38, 15, 0.44],
    },
    faint: {
        width: ['interpolate', ['linear'], ['zoom'], 2, 0.2, 10, 0.35, 14, 0.8, 18, 1.8],
        opacity: ['interpolate', ['linear'], ['zoom'], 2, 0.18, 10, 0.24, 14, 0.34, 18, 0.42],
    },
};

/**
 * يُظهر شبكة الطرق ويُلوّنها لتناسب حقل الطقس تحتها — كما تفعل Windy.
 *
 * **عادت الطرق بطلب صريح.** كانت مُخفاة كلياً (`hideRoadDetail`) مطابقةً لـ Zoom Earth
 * الذي لا يعرض أي طريق داخل الدول. Windy على العكس يعرضها كنسيج خفيف يعطي إحساس
 * المكان، فرجعنا إلى سلوكه.
 *
 * ملاحظتان على التلوين:
 *  1. الطرق تُرسم **فوق** حقل الطقس المعتم، فألوان CARTO الأصلية (رمادي داكن على
 *     خلفية سوداء) تصير خطوطاً موحلة فوق البنفسجي/الفستقي. لذا نفرض اللون بأنفسنا
 *     حسب قاعدة الطبقة: فاتح فوق القاعدة الداكنة، وبنّي داكن فوق الفاتحة.
 *  2. الشفافية منخفضة عمداً وتتصاعد مع التكبير — المطلوب نسيج يُقرأ لا شبكة تُنافس
 *     ألوان الطقس. عند الزووم الإقليمي تظهر الشرايين وحدها.
 */
export function styleRoadDetail(map: MaplibreMap, darkMode: boolean, activeLayer?: string | null): void {
    const layers = map.getStyle()?.layers ?? [];
    const darkBase = layerBaseIsDark(activeLayer, darkMode);
    // فوق القاعدة الداكنة خطوط فاتحة، وفوق الفاتحة خطوط بنّية داكنة (لا سوداء صرفة:
    // الأسود فوق الفستقي يبدو كحدّ إداري ويلتبس بحدود الدول).
    const color = darkBase ? 'rgb(232,238,246)' : 'rgb(84,72,58)';

    for (const layer of layers) {
        if (layer.type !== 'line') continue;
        if (isBoundaryLine(layer)) continue;
        if (!ROAD_LINE_RE.test(layer.id)) continue;

        const tier = roadTier(layer.id);
        if (tier === 'hidden') {
            try { map.setLayoutProperty(layer.id, 'visibility', 'none'); } catch { /* تجاهل */ }
            continue;
        }
        const s = ROAD_STYLE[tier];
        try {
            map.setLayoutProperty(layer.id, 'visibility', 'visible');
            // 0→24: نُلغي حدّ الزووم الذي يضعه ستايل CARTO على كل طبقة طرق، فيصير
            // ظهور الطريق محكوماً ببيانات البلاطة وحدها لا بقرار من الستايل.
            map.setLayerZoomRange(layer.id, 0, 24);
            map.setPaintProperty(layer.id, 'line-color', color as never);
            map.setPaintProperty(layer.id, 'line-width', s.width as never);
            map.setPaintProperty(layer.id, 'line-opacity', s.opacity as never);
            map.setPaintProperty(layer.id, 'line-blur', 0);
        } catch { /* تجاهل */ }
    }
}

/**
 * يرفع **كل** طبقات الحدود (الداخلية والخارجية) لتُرسم أسفل أوّل تسمية نصية مباشرةً.
 * بعض الأنماط (CARTO dark-matter) ترسم الحدود الداخلية أسفل المكدّس (تحت الماء)،
 * فترفعها هذه الدالة لتصبح كلّها متجاورة فوق الخريطة وتحت التسميات — فتظهر فوق طبقة الطقس.
 * تُستدعى مرّة عند تحميل الخريطة.
 */
export function liftAdminBordersAboveBase(map: MaplibreMap): void {
    const layers = map.getStyle()?.layers ?? [];
    const firstSymbolId = layers.find((l) => l.type === 'symbol')?.id;
    if (!firstSymbolId) return;
    for (const layer of layers) {
        if (isBoundaryLine(layer)) {
            try { map.moveLayer(layer.id, firstSymbolId); } catch { /* تجاهل */ }
        }
    }
}

/**
 * يضبط ألوان الخريطة الأساسية لتناسب عرض الطقس (كـ Zoom Earth):
 * - light → خلفية فستقية فاتحة وماء أزرق فاتح. - dark → درجات داكنة هادئة.
 * بعد إدراج طبقة الطقس أسفل **كل** طبقات الخريطة (راجع getWeatherInsertBeforeId)،
 * تصبح مضلّعات اليابسة/الماء/المباني فوق الطقس فتغطّيه كلياً لو بقيت معتمة — لذا
 * تُخفَّض شفافيتها هنا (fill-opacity) فتظهر ألوان الطقس من تحتها مع بقاء الخطوط
 * (طرق/حدود/سواحل) والتسميات معتمة وواضحة فوق الجميع.
 * تعمل عبر setPaintProperty على طبقات الأرض/الماء/المباني الشائعة (آمنة: تتجاهل المفقود).
 */
export function styleWeatherBase(map: MaplibreMap, darkMode: boolean, activeLayer?: string | null, precipGray?: boolean, imagery?: boolean): void {
    // نتحقّق من وجود الطبقة فعلياً قبل التلوين — أنماط مختلفة (CARTO وغيرها) تسمّي
    // طبقات اليابسة/الماء بأسماء مختلفة، وSetPaintProperty على طبقة غير موجودة
    // لا يرمي خطأً بل يُصدر حدث 'error' يُسجَّل في الكونسول (ضوضاء بلا فائدة).
    const set = (id: string, prop: string, val: unknown) => {
        if (!map.getLayer(id)) return;
        // الاسم من جداولنا لا حرفيّ، وMapLibre 6 يطلب مفتاحاً من AllPaintProperties.
        try { map.setPaintProperty(id, prop as never, val as never); } catch { /* تجاهل */ }
    };
    // الوضع الفاتح: قاعدة فستقية (sage) مطابقة لـ Zoom Earth. ألوان الحرارة شبه الشفّافة
    // تمتزج فوق هذه القاعدة فتظهر غنيّة ودقيقة (فوق الأبيض كانت تبهت). الماء بأزرق فاتح خفيف.
    // قاعدة فستقية مُوحَّدة (يابسة + بحر + خلفية) في الوضع الفاتح كـ Zoom Earth — فحيث لم
    // تصل بلاطات الحرارة بعد، تبقى الخريطة فستقية لا زرقاء/داكنة. الداكن للرياح/الأمطار.
    // القاعدة الفاتحة فستقية افتراضياً؛ لطبقة الضغط رمادي فاتح محايد؛ ولطبقتَي الرطوبة
    // ونقطة الندى قاعدة أصفر-أخضر فاتحة (مطابقة Zoom Earth) فتبرز تدرّجاتهما — من البنّي
    // الجاف إلى الأزرق المشبع — بوضوح ومنطقية فوقها بدل أن تبهت على الفستقي الداكن.
    const isHumidityBase = activeLayer === 'humidity' || activeLayer === 'dewpoint';
    const lightTone = activeLayer === 'pressure'
        ? '#e8e6e2'
        : isHumidityBase
            ? '#dde7a0'
            : '#cedb9c';
    // طبقتا الرياح/الهبّات على قاعدة داكنة لكن بلون أزرق بنفسجي (بدل الأسود) لإبراز الجسيمات.
    const isWindBase = activeLayer === 'wind' || activeLayer === 'wind-gusts';
    const windTone = '#514aa8';
    const darkLand  = isWindBase ? windTone : '#20242a';
    const darkWater = isWindBase ? windTone : '#161b22';
    const darkBg    = isWindBase ? windTone : '#0e1116';
    let land  = darkMode ? darkLand  : lightTone;
    let water = darkMode ? darkWater : lightTone;
    let bg    = darkMode ? darkBg    : lightTone;

    // ── قاعدة الهطول ───────────────────────────────────────────────────────────────
    // الهطول حقل **متفرّق**: معظم الخريطة بلا مطر، فما يراه المستخدم هناك هو القاعدة لا
    // الحقل. القاعدة شبه السوداء السابقة كانت تجعل الخريطة فراغاً أسود بين بقع ملوّنة،
    // ولا يُميَّز فيها برّ من بحر.
    //
    // **المبدأ الحاكم: القاعدة منخفضة التشبّع، والبيانات وحدها ملوّنة.**
    // تدرّج المطر يمتدّ من الأزرق الفاتح إلى القرمزي إلى الأصفر — أي أنه يحتلّ معظم دائرة
    // الألوان. أي قاعدة مشبعة تنافسه على انتباه العين وتُفسد قراءة الشدّة. لذا اليابسة
    // زيتونيّ خافت (تشبّع ~15%) والبحر كحليّ منخفض التشبّع، وكلاهما في نطاق سطوع متوسّط
    // إلى داكن — فيبقى الأزرق الفاتح (أضعف فئة مطر) أفتح من كل ما تحته بفارق واضح.
    //
    // الفرق بين اليابسة والبحر مبنيّ على **السطوع** أساساً لا على الصبغة (اليابسة أفتح
    // بنحو ثلاثة أضعاف)، لأن الصبغة محجوزة للبيانات. وهذا أيضاً ما يجعل الخريطة تُقرأ
    // لمن لا يميّز الأحمر عن الأخضر.
    //
    // ملاحظة صادقة: Zoom Earth يستعمل قاعدة صور أقمار حقيقية (صحارى رملية، غابات داكنة)؛
    // لدينا نغمة واحدة لليابسة لأن بلاطات CARTO لا تصنّف الغطاء الأرضي عالمياً — نعوّض
    // عنها بظلّ التضاريس فوق اليابسة (راجع enforceWeatherStackOrder).
    const isPrecipBase = activeLayer === 'precipitation';
    const isStormBase = activeLayer === 'k-index' || activeLayer === 'o-index';
    /*
     * ── قاعدة شذوذ سطح البحر ────────────────────────────────────────────────────
     *
     * أقرب إلى الهطول منها إلى الحرارة، لسببٍ بنيويّ لا ذوقيّ: هذه الطبقة **لا تغطّي
     * اليابسة إطلاقاً** — حقل بحري تُخفي يابستَه بقناع البرّ. فكل لون تراه العين على
     * القارّات مصدره القاعدة وحدها، تماماً كما في الهطول حيث معظم الخريطة بلا مطر.
     *
     * ومعاملتها كطبقة «تغطية كاملة» (وهو ما كان يحدث: لا حالة لها هنا فسقطت على
     * الافتراضي) تُصفّر شفافية مضلّعات اليابسة والماء معاً، فلا يبقى على القارّات إلا
     * خلفية ‏#0e1116 — **قارّات سوداء تماماً**، وهو ما رُصد.
     *
     * (البحر نفسه صار معتماً بعد أن غدا الصفر أبيضَ معتماً في SST_ANOMALY_STOPS، فلون
     * الماء أدناه لا يُرى إلا في اللحظات السابقة لوصول أوّل إطار. أمّا اليابسة فيُرى
     * لونها دائماً — وهي مربط الفرس هنا.)
     *
     * الألوان هي ألوان الهطول نفسها لا نسخة قريبة منها: نفس المبدأ يحكمهما (قاعدة
     * منخفضة التشبّع، والصبغة محجوزة للبيانات)، والتدرّج هنا أزرق↔أحمر يحتلّ طرفَي
     * دائرة الألوان فأي قاعدة مشبعة تنافسه. والاتّساق بين الطبقتين مقصود: انتقال
     * المستخدم بينهما لا يُغيّر شكل الأرض تحته.
     */
    const isSstBase = activeLayer === 'sst-anomaly';
    /*
     * قاعدة الغيوم — **داكنة عمداً، وهي نصف الطبقة لا زينتها.**
     *
     * حقل الغيوم أبيضُ خالص بشفافية متغيّرة (راجع CLOUD_HEATMAP_STOPS)، فما يُرى منه
     * ليس لونه بل **تباينه مع ما تحته**. وقاعدةٌ فاتحة تجعل الأبيض على الأبيض: تختفي
     * الغيوم الرقيقة تماماً، وتبدو الكثيفة رمادية باهتة. وهذا ما كان يحدث.
     *
     * والداكن هنا أزرق مائل للرمادي لا أسود: الأسود يجعل الحوافّ الشفّافة تُقرأ
     * دخاناً، بينما الأزرق الداكن يُقرأ «ليل/محيط» فتبدو الغيمة فوقه غيمةً — وهو
     * ما تفعله كل خرائط الغيوم من Zoom Earth إلى صور الأقمار الليلية.
     */
    const isCloudBase = activeLayer === 'clouds';
    /*
     * ── قاعدة الرادار — رماديّ محايد، وهي شرطُ رؤية المطر الخفيف لا زينة ────────
     *
     * تدرّج الرادار (المطابق لتدرّج Windy، راجع `src/config/radarPalette.json`) يبدأ عند
     * ‏0–14 dBZ بأزرق-بنفسجيّ **داكن وشفّاف جزئياً** ثم يتصاعد إلى القرمزيّ. أي أن أضعف
     * فئة — وهي الأكثر مساحةً على الشاشة — أغمقُ من كل ما عداها.
     *
     * وقاعدةٌ داكنة تحته تعني داكناً على داكن: يختفي المطر الخفيف تماماً، ولا يُرى إلّا
     * قلب الخلية. وهذا بالضبط ما كان يحدث: الرادار لم تكن له حالة هنا فسقط على الداكن
     * العامّ ‏#0e1116 بمضلّعات شبه شفّافة — «خلفية سوداء» بتعبير الرصد. وهو تكرارٌ حرفيّ
     * لعطب طبقة شذوذ سطح البحر الموثَّق أدناه، ومن السبب نفسه: طبقة تدخل بلا حالة قاعدة.
     *
     * ولهذا يستعمل Windy قاعدة رمادية فاتحة في عرض الرادار تحديداً وحدها دون بقيّة
     * طبقاته. نأخذ المبدأ لا القيمة: رماديّ محايد **متوسّط السطوع** يناسب سمة التطبيق
     * الداكنة، وهو أفتح من كل ما دون 20 dBZ فيظهر المطر الخفيف عليه.
     *
     * ومحايدٌ لا مصبوغ لأن التدرّج يمرّ بالأزرق والأخضر والأصفر والأحمر والقرمزي — أي
     * دائرة الألوان كاملة. فلا صبغة تبقى شاغرةً للقاعدة. (نفس حجّة شذوذ سطح البحر،
     * ونفس الزوج اللوني تماماً — والاتّساق مقصود.)
     */
    const isRadarBase = activeLayer === 'radar';

    if (isPrecipBase && precipGray) {
        // قالب الألوان الثاني لطبقة الأمطار: خلفية رمادية محايدة بدل الزيتوني/الكحلي —
        // طلب صريح لتمييز القالب بصرياً، بنفس مبدأ "قاعدة منخفضة التشبّع" أعلاه لكن
        // بصبغة رمادية محايدة تماماً بدل الزيتوني.
        land = '#55565c';
        water = '#2c2d31';
        bg = '#55565c';
    } else if (isPrecipBase) {
        land = '#4c5a44';    // زيتونيّ خافت — كان #5c7248 مشبعاً ينافس ألوان المطر
        water = '#13273c';   // كحليّ عميق منخفض التشبّع
        bg = '#4c5a44';      // في CARTO الخلفية هي اليابسة، ومضلّع الماء يُرسم فوقها
    } else if (isStormBase) {
        // قاعدة مؤشرات العواصف: خلفية داكنة (Slate / Navy) تبرز ألوان العواصف (الأصفر/الأحمر/الأرجواني)
        land = '#363d4d';
        water = '#1c2433';
        bg = '#363d4d';
    } else if (isRadarBase) {
        land = '#55565c';
        water = '#2c2d31';
        bg = '#55565c';
    } else if (isCloudBase) {
        land = '#2a303c';    // رمادي مزرقّ داكن — يُبرز الأبيض بلا أن يبتلع حوافّه
        water = '#141920';   // أعمق قليلاً كي يبقى البرّ مميَّزاً عن البحر تحت الغيوم
        bg = '#2a303c';
    } else if (isSstBase) {
        /*
         * **رماديّ محايد لا زيتونيّ.** الزيتوني يعمل تحت الهطول لأن تدرّجه أزرق→أصفر
         * يترك الأخضر شاغراً. أمّا هنا فالتدرّج أزرق↔**أبيض**↔أحمر، والأبيض عند الصفر
         * هو أهمّ لون فيه — فأي صبغة على اليابسة تصير لوناً ثالثاً يزاحم قراءة «طبيعي»
         * ويجعل الخريطة ثلاثية الصبغات بلا داعٍ.
         *
         * والرمادي هو ما تستعمله كل المراجع (‏NOAA وCopernicus وNASA) لليابسة في خرائط
         * الشذوذ، وللسبب نفسه: الصبغة محجوزة للبيانات، واليابسة «ليست بيانات».
         * وسطوعه المتوسّط يفصله عن الأبيض (لا شذوذ) وعن الأزرق الداكن (شذوذ سالب حادّ).
         */
        land = '#55565c';
        water = '#2c2d31';
        bg = '#55565c';
    }

    set('background', 'background-color', bg);
    // ملاحظة مهمة (مطابقة Zoom Earth): طبقة الطقس مُدرَجة أسفل مضلّعات اليابسة/الماء.
    // لو بقيت هذه المضلّعات معتمة (ولو جزئياً) فإنها تكتم لون الطقس من تحتها — وأوضحها
    // مضلّع الماء فوق المحيطات، فيظهر البحر "شبه فارغ" بينما تظهر اليابسة ملوّنة.
    // الحلّ: نجعلها شفّافة تماماً فيغطّي حقل الطقس البرّ والبحر بالتساوي، ويبقى خطّ
    // الساحل (continent-coastline) والحدود والتسميات فوقه كخطوط واضحة.
    // الطبقات كاملة التغطية (حرارة/رياح/ضغط/رطوبة…) تُرسم معتمة 100% مثل Zoom Earth —
    // أي صبغة يابسة/ماء فوقها تكسو الألوان وتبهتها، فنجعل المضلّعات شفّافة تماماً معها.
    // الطبقات المتفرّقة (أمطار/عواصف/غيوم) تحتاج تمييز البرّ/البحر فنُبقي لمسة خفيفة.
    // في وضع الهطول/العواصف تصير المضلّعات **معتمة**: الحقل يُرفع فوقها (setSparseOverlay)
    // فلا تكتمه، وبذلك نكسب قاعدة جغرافية حقيقية بدل صبغة بنسبة 10% لا تكاد تُرى.
    // الرادار متفرّق كالهطول تماماً — بل أشدّ: تغطيته الجغرافية أمريكية، فمعظم الخريطة
    // بلا بيانات. فيجب أن تبقى مضلّعات اليابسة/الماء مرئية لتُقرأ الجغرافيا تحته.
    const isSparseLayer = isPrecipBase || isStormBase || isSstBase || isCloudBase || isRadarBase;
    const fullCoverage = !!activeLayer && !isSparseLayer;
    const landAlpha = isPrecipBase || isSstBase || isRadarBase ? 0.55 : isStormBase ? 0.65 : isCloudBase ? 0.75 : fullCoverage ? 0 : 0.10;
    const waterAlpha = isPrecipBase || isStormBase || isSstBase || isCloudBase || isRadarBase ? 1 : fullCoverage ? 0 : 0.12;

    /*
     * فوق الصور الجوّية: مضلّعات الأساس **شفّافة تماماً**.
     *
     * وإلّا صارت الصور تحت طبقتين تحجبانها — يابسةٌ ملوّنة وماءٌ ملوّن — فلا يُرى
     * منها شيء ولا معنى لتحميلها. والخطوط والتسميات تبقى كما هي: هي المعلومة التي
     * تُقرأ فوق الصورة (طريق، حدّ مقاطعة، اسم مدينة)، وهي بالضبط ما يفعله المرجع.
     */
    if (imagery) {
        set('background', 'background-color', 'rgba(0,0,0,0)');
        for (const l of ['landcover', 'landuse', 'landuse_residential', 'landcover_wood',
            'landcover_grass', 'landcover_sand', 'landcover_ice', 'park', 'national_park',
            'water', 'water_shadow', 'ocean', 'building', 'building-top']) {
            set(l, 'fill-opacity', 0);
        }
        return;
    }

    for (const l of ['landcover', 'landuse', 'landuse_residential', 'landcover_wood',
                     'landcover_grass', 'park', 'park_national_park', 'park_nature_reserve',
                     'national_park', 'wood', 'grass', 'globallandcover']) {
        // في وضع الهطول تُترك رقعُ الغابات/المروج أغمق قليلاً من الخلفية فتعطي اليابسة
        // تنوّعاً بسيطاً بدل لوح أخضر مسطّح.
        set(l, 'fill-color', isPrecipBase ? '#414d3b' : isStormBase ? '#2b313e' : isSstBase ? '#4e4f55' : isCloudBase ? '#252b36' : land);
        set(l, 'fill-opacity', landAlpha);
    }
    for (const l of ['water', 'water_shadow', 'ocean']) {
        set(l, 'fill-color', water);
        set(l, 'fill-opacity', waterAlpha);
    }
    for (const l of ['building', 'building-top']) {
        set(l, 'fill-opacity', 0.5);
    }

    // الحقل يُرفع فوق المضلّعات المعتمة — بدونه يكسو مضلّعُ الماء (‏alpha=1) الشذوذَ
    // من فوقه فيختفي البحر الملوّن كلّه.
    // الغيوم معهم: بلا الرفع تبقى مضلّعات البرّ/البحر **فوق** الحقل فتكتم أبيضه،
    // وهو أشدّ ما يكون أثراً هنا لأن الحقل شفّاف أصلاً فلا يحتمل حجاباً إضافياً.
    setSparseOverlay(map, isPrecipBase || isStormBase || isSstBase || isCloudBase);
}

/**
 * معرّفات تمريرات الماء المسجَّلة.
 *
 * **سبب وجود السجلّ بدل المسح:** `map.getStyle().layers` **لا يُدرج الطبقات المخصّصة
 * إطلاقاً** — الستايل يُسلسَل، والطبقة المخصّصة كائن JS غير قابل للتسلسل. أُثبت ذلك
 * بقياس مباشر في MapLibre 5: `getLayer('weather-heatmap-scalar-sea')` يُرجع الطبقة
 * بينما `getStyle().layers` لا تحوي اسمها.
 *
 * وكان enforceWeatherStackOrder يبحث عن تمريرات الماء بمسح تلك القائمة، فلا يجدها أبداً
 * ⇒ لا تُرفع فوق التضاريس ⇒ **يبقى ظلّ التضاريس على البحر رغم وجود التمريرة**. هذا هو
 * سبب بقاء المشكلة بعد ثلاث محاولات. السجلّ يجعل الترتيب مستقلّاً عن هذا القيد.
 */
const seaPassIds = new Set<string>();

export function registerSeaPass(id: string): void { seaPassIds.add(id); }
export function unregisterSeaPass(id: string): void { seaPassIds.delete(id); }

/**
 * مضلّعات الماء الداخلية (بحيرات/أنهار/سدود/مسطّحات ملحية) تُستثنى من خطّ الساحل.
 *
 * بدونها يُرسم الخطّ حول **كل** مضلّع ماء في الخريطة الأساسية، فتظهر أشكال سوداء متناثرة
 * داخل اليابسة (بحيرات المغرب، سبخات وادي الكبير قرب قادس…). المطلوب حافّة القارّة وحدها.
 *
 * الصياغة بالنفي لا بالإثبات عمداً: لو كان الحقل `class` غائباً في نسخة بلاطات أخرى،
 * فإن `['==', class, 'ocean']` كانت ستُطابق لا شيء = اختفاء خطّ الساحل كلّه. أمّا النفي
 * فمع الحقل الغائب يُرجع "غير مطابق" ⇒ يبقى كل شيء مرسوماً. الفشل هنا آمن لا كارثي.
 */
const COASTLINE_FILTER = ['!',
    ['in', ['get', 'class'],
        ['literal', ['lake', 'river', 'pond', 'reservoir', 'dock', 'swimming_pool', 'canal', 'stream']]],
];

/**
 * يرسم خطّ سواحل القارات (حافة اليابسة حيث تلتقي بالبحر) من **مضلّعات الخريطة الأساسية
 * المتّجهة**، فيظهر شكل كل يابسة بوضوح.
 *
 * **عاد إلى المتّجهات بطلب صريح.** كان قد استُبدل بخطّ يُرسم في الشيدر من landmask.png
 * كي يتطابق مع حدّ اللون بالبناء (مصدر واحد لا مصدران). لكن ثمن ذلك كان شكل الخطّ نفسه:
 * القناع يحسم عند ~10 كم، فالخطّ يفقد تفاصيل الساحل الحقيقية ويصير مضلّعاً عند التكبير،
 * بينما البلاطات المتّجهة تبقى حادّة عند أي تكبير.
 *
 * الأثر الجانبي المقبول: خطّ الساحل وحدّ اللون من مصدرين مختلفين، فقد لا ينطبقان تماماً
 * عند التكبير العميق — وهو ما كان قائماً قبل ذلك التغيير.
 */
export function addContinentCoastline(map: MaplibreMap, darkMode: boolean, activeLayer?: string | null): void {
    const layers = map.getStyle()?.layers ?? [];
    const coastColor = forcesBlackBorders(activeLayer)
        ? 'rgba(0, 0, 0, 1)'
        : (darkMode ? 'rgba(255, 255, 255, 1)' : 'rgb(0, 0, 0,1)');

    if (map.getLayer('continent-coastline')) {
        try { map.setPaintProperty('continent-coastline', 'line-color', coastColor); } catch { /* تجاهل */ }
        try { map.setFilter('continent-coastline', COASTLINE_FILTER as never); } catch { /* تجاهل */ }
        return;
    }

    // نأخذ مصدر/طبقة الماء من أول طبقة ماء موجودة لرسم حدودها كخطّ ساحل.
    const waterFill = layers.find((l) =>
        l.type === 'fill' && (l.id === 'water' || /(^|[_-])water([_-]|$)|ocean/i.test(l.id))
    ) as ({ source?: string; 'source-layer'?: string } | undefined);
    if (!waterFill || !waterFill.source) return;

    const beforeId = layers.find((l) => l.type === 'symbol')?.id; // فوق الطقس والحدود، تحت التسميات
    try {
        map.addLayer({
            id: 'continent-coastline',
            type: 'line',
            source: waterFill.source,
            'source-layer': waterFill['source-layer'],
            filter: COASTLINE_FILTER,
            paint: {
                'line-color': coastColor,
                'line-opacity': ['interpolate', ['linear'], ['zoom'], 3, 0.75, 6, 0.85],
                'line-width': ['interpolate', ['linear'], ['zoom'], 1, 0.4, 4, 0.7, 8, 1.1],
            },
        } as never, beforeId);
    } catch { /* تجاهل */ }
}

/**
 * يُخفي كل تسميات الخريطة الأساسية الإنجليزية (طبقات الرموز النصية).
 * الموقع عربي بالكامل، والتسميات العربية تأتي من overlay مخصّص (ArabicCityLabels).
 * تُستثنى طبقات الرموز التي نضيفها نحن (تسميات الإيزوبار) — أرقام ضغط لا أسماء إنجليزية،
 * وهذه الدالة تُستدعى عند كل تبديل ثيم/طبقة فكانت ستُخفيها نهائياً.
 *
 * ── الاستثناء بالمصدر لا بالاسم ───────────────────────────────────────────────
 * كان الاستثناء `‎/^isobars?-/‎` على **مُعرّف** الطبقة، وطبقتا النصوص اسمهما
 * `pressure-centres` و`pressure-value-labels` — فلا تُطابقانه، فتُخفَيان مع تسميات
 * الخريطة الإنجليزية. النتيجة التي شوهدت: الخطوط مرسومة (شيدر لا رموز، فلا تمسّه
 * هذه الدالة) بلا حرف H أو L ولا رقم واحد، مع أنّ الحساب سليم — قيس في الإنتاج:
 * 76 معلماً في المصدر و`visibility: none` على الطبقتين. وتظهر عند إطفاء الطبقة
 * وإعادتها لأنّ react-map-gl يُعيد إضافتها بالظهور الافتراضي بعد آخر استدعاء هنا.
 *
 * فصار الاستثناء بالمصدر: كل ما يخرج من `pressure-annotations` ملكنا مهما سُمّي،
 * فلا يعود تغييرُ اسمِ طبقةٍ يُخفي نصوصها بصمت. والاسم القديم باقٍ احتياطاً.
 */
const OWN_SYMBOL_SOURCES = new Set(['pressure-annotations']);

export function hideBaseMapLabels(map: MaplibreMap): void {
    const layers = map.getStyle()?.layers ?? [];
    for (const layer of layers) {
        if (layer.type !== 'symbol') continue;
        const ours = /^isobars?-/.test(layer.id)
            || ('source' in layer && typeof layer.source === 'string' && OWN_SYMBOL_SOURCES.has(layer.source));
        if (ours) continue;
        try { map.setLayoutProperty(layer.id, 'visibility', 'none'); } catch { /* تجاهل */ }
    }
}

/**
 * يُلوّن كل الحدود الإدارية (دول/ولايات/محافظات) حسب الثيم لتكون واضحة فوق الطبقة:
 * - light → أسود.  - dark → أبيض شفّاف مناسب.
 * حدود الدول أثخن وأوضح من الحدود الداخلية.
 */
/**
 * الطبقات التي تُظهر الحدود الداخلية (ولايات/محافظات/مقاطعات).
 *
 * الرادار وحده اليوم — انظر التعليل عند نقطة الاستدعاء في styleAdminBorders.
 */
function showsInnerBoundaries(activeLayer: string | null | undefined): boolean {
    return activeLayer === 'radar';
}

/**
 * تُميَّز رتبة الحدّ الداخلي بمعرّف طبقة CARTO مباشرةً.
 *
 * ‏`boundary_state` هي admin_level 4 و`boundary_county` هي 6 في نمط dark-matter —
 * وهذه أسماء ثابتة في الستايل الذي نحمّله بعينه، فقراءتها أدقّ وأرخص من إعادة
 * تفسير الفلتر.
 */
function innerBoundaryTier(id: string): 'state' | 'county' {
    return /county|admin[_-]?6|level[_-]?6/i.test(id) ? 'county' : 'state';
}

/**
 * يرسم حدّاً داخلياً بوزنٍ **أدنى من حدود الدول**.
 *
 * التدرّج مقصود: بلا فرق في الوزن تصير الخريطة شبكةً متساوية الخطوط لا يُقرأ منها
 * أيّ حدٍّ أهمّ — وهو أسوأ من إخفائها جميعاً. الدولة أعتم وأثخن، ثمّ الولاية، ثمّ
 * المقاطعة أخفتها.
 *
 * والمقاطعات تبدأ من التكبير 7 لا من 0: عند مستوى القارّة تُرسم آلاف الخطوط فوق
 * بعضها فتصير اليابسة رماديةً صلبة تحجب الصدى — وهو نقيض الغرض. (‏CARTO نفسه يبدأها
 * من 9؛ خفّضناها إلى 7 لأن العاصفة تُتابَع من ارتفاع أعلى ممّا يُتصفَّح به الشارع.)
 */
function styleInnerBoundary(map: MaplibreMap, id: string, darkMode: boolean): void {
    const county = innerBoundaryTier(id) === 'county';
    const color = darkMode
        ? (county ? 'rgba(196,206,220,0.42)' : 'rgba(214,224,238,0.62)')
        : (county ? 'rgba(0,0,0,0.30)' : 'rgba(0,0,0,0.46)');
    const width = county
        ? ['interpolate', ['linear'], ['zoom'], 7, 0.2, 10, 0.4, 14, 0.7]
        : ['interpolate', ['linear'], ['zoom'], 3, 0.25, 6, 0.4, 10, 0.7];
    try {
        map.setLayoutProperty(id, 'visibility', 'visible');
        map.setLayerZoomRange(id, county ? 7 : 3, 24);
        map.setPaintProperty(id, 'line-color', color as never);
        map.setPaintProperty(id, 'line-opacity', 1);
        map.setPaintProperty(id, 'line-width', width as never);
        map.setPaintProperty(id, 'line-blur', 0);
        // ‏CARTO يرسم حدود المقاطعات متقطّعة؛ الصلب أهدأ تحت حقلٍ ملوّن مزدحم.
        map.setPaintProperty(id, 'line-dasharray', [1, 0] as never);
    } catch { /* تجاهل */ }
}

export function styleAdminBorders(map: MaplibreMap, darkMode: boolean, activeLayer?: string | null): void {
    const layers = map.getStyle()?.layers ?? [];
    const forceBlack = forcesBlackBorders(activeLayer);
    for (const layer of layers) {
        if (!isBoundaryLine(layer)) continue;
        const isCountry = isCountryBoundary(layer.id);
        // مطابقة Zoom Earth: نعرض حدود الدول فقط ونُخفي الحدود الداخلية (ولايات/محافظات)
        // كي لا تظهر خطوط زائدة داخل الدول لا توجد في Zoom Earth.
        //
        // **إلّا للرادار.** وهو استثناء مبنيّ على فرق في وظيفة الطبقة لا في الذوق:
        // خريطة الحرارة تُقرأ بالتدرّج فيكفيها شكل القارّة، أمّا الرادار فسؤاله
        // «هل العاصفة فوق مدينتي؟» — وجوابه يحتاج مرجعاً أرضياً دقيقاً. ولهذا تعرض
        // كل تطبيقات الرادار المتخصّصة (‏RadarScope وMyRadar) حدود المقاطعات تحت
        // الصدى: بلا شبكة مرجعية تصير البقعة الحمراء **جميلة وبلا معنى**.
        if (!isCountry) {
            if (!showsInnerBoundaries(activeLayer)) {
                try { map.setLayoutProperty(layer.id, 'visibility', 'none'); } catch { /* تجاهل */ }
                continue;
            }
            styleInnerBoundary(map, layer.id, darkMode);
            continue;
        }
        // حدود الدول: أسود (نهاري) / أبيض (داكن) — بارزة.
        // طبقات الحرارة/الرياح/الرطوبة/الضغط: أسود ثابت بلا اعتماد على الثيم.
        const color = forceBlack
            ? 'rgba(0,0,0,0.88)'
            : (darkMode ? 'rgba(235,238,242,0.9)' : 'rgba(0,0,0,0.88)');
        // خطوط شعرية رفيعة جداً (كـ Zoom Earth)
        const width = ['interpolate', ['linear'], ['zoom'], 2, 0.25, 5, 0.45, 9, 0.75];
        try {
            map.setLayoutProperty(layer.id, 'visibility', 'visible');
            map.setLayerZoomRange(layer.id, 0, 24);
            map.setPaintProperty(layer.id, 'line-color', color);
            map.setPaintProperty(layer.id, 'line-opacity', 1);
            // ‏width مصفوفةٌ حرفية يُستنتج نوعها عامّاً لا تعبيرَ MapLibre (تشدّد أنواع الإصدار 6).
            map.setPaintProperty(layer.id, 'line-width', width as never);
            map.setPaintProperty(layer.id, 'line-blur', 0);
        } catch { /* تجاهل */ }
    }

    // نفس نقطة الاستدعاء التي كان يشغلها hideRoadDetail سابقاً: تُنفَّذ عند كل تبديل
    // ثيم/طبقة، فيتبع لون الطرق قاعدة الطبقة الجديدة فوراً.
    styleRoadDetail(map, darkMode, activeLayer);
}


// ─────────────────────────────────────────────────────────────────────────────
// التضاريس (hillshade) — تظهر تدريجياً مع التكبير كما في Windy و Zoom Earth
// ─────────────────────────────────────────────────────────────────────────────

const DEM_SOURCE_ID = 'terrain-dem';
export const HILLSHADE_LAYER_ID = 'terrain-hillshade';

/**
 * بلاطات الارتفاع من AWS Terrain Tiles (Mapzen سابقاً) — مفتوحة بلا مفتاح ولا تسجيل،
 * بترميز terrarium‏ (h = R*256 + G + B/256 − 32768 متراً)، 256×256 بكسل.
 *
 * تُطلب عبر نطاقنا (‎/terrain/‎) لا من s3.amazonaws.com مباشرةً. السبب مرصود لا وقائي:
 * الطلب المباشر يفشل عند بعض المستخدمين بـ ERR_CONNECTION_RESET و ERR_EMPTY_RESPONSE —
 * تدخّلٌ على مسار S3 يختلف باختلاف الشبكة والمزوّد، فيسقط ظلّ التضاريس عندهم وحدهم
 * ويمتلئ سجلّ الطرفية بأخطاء AJAXError. وحتى حيث ينجح، قِيس زمن البلاطة الواحدة
 * 3.6-5.4 ثانية لحجم 84 كيلوبايت — بطءٌ يؤخّر أوّل ظلّ تضاريس في كل زيارة.
 *
 * التمرير عبر نطاقنا يعالج الأمرين معاً: المسار يصير مسار الموقع نفسه (فما دام الموقع
 * يفتح فالبلاطات تصل)، والاستجابة تُخزَّن على حافّة Cloudflare فتُخدَّم لاحقاً من أقرب
 * نقطة. البلاطات ثابتة لا تتغيّر، فالكاش الطويل بلا مخاطرة.
 * كتلة الوكيل في deploy/nginx-skyradarweather.conf تحت `location /terrain/`.
 */
const DEM_TILES = '/terrain/{z}/{x}/{y}.png';

/**
 * أعلى زووم نطلب عنده بلاطات ارتفاع فعلية؛ ما فوقه يُكبَّر MapLibre نفس البلاطة.
 *
 * خُفِّض من 11 إلى 9 بعد قياس حجم الحمولة (‏21 ميغابايت في Lighthouse): كل بلاطة ~120
 * كيلوبايت، وكل مستوى زووم يُضاعف عددها أربع مرّات. عند 9 تكون الدقّة ~300 متر للبكسل —
 * وهي أدقّ بكثير ممّا يحتاجه **ظلّ** تضاريس تحت حقل طقس دقّته 22 كم. الفرق البصري لا
 * يكاد يُرى، والتوفير عند التكبير العميق ستّة عشر ضعفاً.
 */
const DEM_MAX_ZOOM = 9;

/**
 * شدّة الظلّ حسب الزووم. صفر تحت 4 (عند رؤية العالم كلّه لا معنى لتضاريس بعرض
 * بكسل واحد، وهي ضوضاء بصرية فوق حقل الطقس)، ثم تتصاعد حتى تثبت عند التكبير القريب.
 * التدرّج مقصود: المستخدم يرى التضاريس "تظهر" وهو يكبّر، لا تقفز فجأةً.
 */
const HILLSHADE_EXAGGERATION: unknown = [
    'interpolate', ['linear'], ['zoom'],
    4, 0,
    5, 0.30,
    7, 0.48,
    9, 0.58,
    12, 0.62,
];

/**
 * ألوان الظلّ/الإضاءة حسب قاعدة الطبقة. الطبقات ذات القاعدة الفاتحة (حرارة/رطوبة…)
 * تحتاج ظلّاً بنّياً دافئاً كي لا يبدو رمادياً ميتاً فوق الفستقي، والداكنة تحتاج ظلّاً
 * أسود أعمق وإضاءة أخفت كي لا تُبيّض ألوان الرياح الزاهية.
 */
function hillshadePaint(darkBase: boolean): Record<string, unknown> {
    return {
        // igor: طريقة ظلّ ألطف على الألوان تحتها من standard — تحافظ على تشبّع حقل
        // الطقس بدل أن تكسوه رمادياً، وهي بالضبط ما يُراد من تضاريس فوق خريطة ملوّنة.
        'hillshade-method': 'igor',
        'hillshade-exaggeration': HILLSHADE_EXAGGERATION,
        // 'map': اتجاه الإضاءة مربوط بالشمال، فلا تنقلب الجبال إلى أودية عند تدوير الخريطة.
        'hillshade-illumination-anchor': 'map',
        'hillshade-illumination-direction': 315,
        'hillshade-shadow-color': darkBase ? 'rgba(0,0,0,0.55)' : 'rgba(74,56,38,0.42)',
        'hillshade-highlight-color': darkBase ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.45)',
        // accent يرسم حوافّ حادّة عند انكسارات الميل؛ فوق حقل طقس ملوّن يظهر كخطوط
        // سوداء مفتعلة، فنُلغيه ونكتفي بالظلّ/الإضاءة كما تفعل Zoom Earth.
        'hillshade-accent-color': 'rgba(0,0,0,0)',
    };
}

/**
 * يضيف طبقة التضاريس (أو يُحدّث ألوانها إن كانت موجودة).
 *
 * الموضع في المكدّس مقصود: **فوق** حقل الطقس و**تحت** السواحل والحدود والتسميات —
 * وهو ترتيب Zoom Earth نفسه (راجع تعليق PRESSURE_HEATMAP_STOPS في colorScales.ts):
 * الظلّ يعطي الحقل ملمس الأرض تحته، بينما تبقى الخطوط والأسماء حادّة فوق الجميع.
 *
 * ملاحظة صادقة: بلاطات terrarium تتضمّن أعماق البحار أيضاً، فيظهر ظلّ خفيف على قاع
 * المحيط وحافّة الجرف القارّي. أثره ضعيف عند هذه الشدّات (قاع البحر أملس ومنخفض
 * التردّد)، ولا سبيل لإخفائه بقناع لأن طبقة hillshade لا تقبل قناعاً — الحلّ الوحيد
 * لو أزعج هو خفض الشدّة أو الاستغناء عن الطبقة.
 */
export function addTerrainHillshade(map: MaplibreMap, darkMode: boolean, activeLayer?: string | null): void {
    // فوق صور القمر الصناعي **لا ظلّ تضاريس**: الصورة تحمل إضاءة الشمس الحقيقية وظلال
    // الجبال الفعلية أصلاً، فإضافة ظلّ محسوب فوقها تُظلم اليابسة مرّتين — حجاب داكن على
    // البرّ دون البحر، وهو تشويه لا تحسين. Zoom Earth لا يضع تضاريس على طبقة الأقمار.
    //
    // والرادار مثلها منذ صارت تحته صورٌ جوّية (setAerialImagery): نفس العلّة حرفياً،
    // وظلٌّ محسوب فوق صورةٍ تحمل ظلّها الحقيقي يُعتم الحقول والمدن بلا معلومة.
    if (activeLayer === 'satellite' || activeLayer === 'radar') {
        if (map.getLayer(HILLSHADE_LAYER_ID)) {
            try { map.setLayoutProperty(HILLSHADE_LAYER_ID, 'visibility', 'none'); } catch { /* تجاهل */ }
        }
        return;
    }
    if (map.getLayer(HILLSHADE_LAYER_ID)) {
        try { map.setLayoutProperty(HILLSHADE_LAYER_ID, 'visibility', 'visible'); } catch { /* تجاهل */ }
    }
    // قاعدة الهطول زيتونيّ متوسّط السطوع لا داكنة: ظلّ أسود بشدّة 0.55 عليها يبدو وحلاً،
    // وإضاءة بيضاء بشدّة 0.16 لا تُرى. فنستعمل معالجة القاعدة الفاتحة (ظلّ بنّي دافئ
    // وإضاءة أظهر) — وهي مضبوطة لهذا النطاق من السطوع بالضبط. لا نغيّر layerBaseIsDark
    // نفسها لأنها تحكم أيضاً لون الطرق وسطوع الجسيمات، وكلاهما صحيح على حاله هنا.
    const darkBase = activeLayer === 'precipitation' ? false : layerBaseIsDark(activeLayer, darkMode);

    if (map.getLayer(HILLSHADE_LAYER_ID)) {
        const paint = hillshadePaint(darkBase);
        for (const [prop, val] of Object.entries(paint)) {
            // hillshade-method و illumination-anchor خصائص طلاء لا تخطيط في MapLibre،
            // فكلّها تمرّ عبر setPaintProperty.
            try { map.setPaintProperty(HILLSHADE_LAYER_ID, prop as never, val as never); } catch { /* تجاهل */ }
        }
        return;
    }

    try {
        if (!map.getSource(DEM_SOURCE_ID)) {
            map.addSource(DEM_SOURCE_ID, {
                type: 'raster-dem',
                tiles: [DEM_TILES],
                encoding: 'terrarium',
                tileSize: 256,
                maxzoom: DEM_MAX_ZOOM,
                attribution: '<a href="https://registry.opendata.aws/terrain-tiles/">AWS Terrain Tiles</a>',
            } as never);
        }
    } catch { return; }   // بلا مصدر ارتفاع لا معنى لإضافة الطبقة

    // تُدرَج داخل مجموعة طبقات الطقس (أسفل مرساة الخريطة الأساسية) لا فوقها، ثم
    // enforceWeatherStackOrder يضبط موضعها بينها. لو أُدرجت فوق المرساة لوقعت فوق
    // الجسيمات وفوق تمريرة البحر معاً، فلا يمكن قصر الظلّ على اليابسة.
    const beforeId = getWeatherInsertBeforeId(map);

    try {
        map.addLayer({
            id: HILLSHADE_LAYER_ID,
            type: 'hillshade',
            source: DEM_SOURCE_ID,
            // لا بلاطات ارتفاع تحت هذا الزووم — الشدّة صفر هناك، فطلبها هدر نطاق صرف.
            // 4: أبكر ممّا كان (4.8) كي تظهر التضاريس مع أوّل تكبير إقليمي كما في Windy.
            minzoom: 4,
            paint: hillshadePaint(darkBase),
        } as never, beforeId);
        enforceWeatherStackOrder(map);
    } catch { /* تجاهل */ }
}

/**
 * هل حقل الطقس الحالي «تراكب متفرّق» يُرسم فوق مضلّعات الأساس؟
 *
 * الحقول كاملة التغطية (حرارة/رياح/ضغط…) تُرسم **تحت** المضلّعات ثم تُجعل المضلّعات
 * شفّافة — فلا فرق عملياً. أمّا الهطول فمعظم الخريطة تحته بلا مطر، فيحتاج قاعدة معتمة
 * يراها المستخدم؛ والقاعدة المعتمة تحجب الحقل ما لم يُرفع الحقل فوقها. لذا وضعان.
 *
 * علَم على مستوى الوحدة لا مُعامل: getWeatherInsertBeforeId تُستدعى من مواضع كثيرة لا
 * تعرف الطبقة الفعّالة (كل addLayer وكل enforceWeatherStackOrder)، وتمريره إليها جميعاً
 * كان سيغيّر تواقيع نصف الملف مقابل لا شيء.
 */
let sparseOverlay = false;

/** معرّفات طبقات حقل الطقس اللونية (لا التضاريس ولا الجسيمات). */
const FIELD_LAYER_IDS = ['weather-heatmap-scalar', 'weather-heatmap-wind'];

/** خطوط تساوي الضغط — فوق الحقل اللوني والتضاريس وتمريرة البحر، وتحت الجسيمات.
 *  فوق الحقل لأنها تُقرأ عليه، وتحت الجسيمات لأن الجسيمات هي أوضح ما في المشهد
 *  البحري ولا يجوز أن يقطعها خطّ ثابت. */
export const ISOBAR_LAYER_ID = 'weather-isobars';

/** أنيميشن الهطول (شيدر المطر/الثلج) — يُرسم فوق حقل اللون وتمريرة البحر وتحت الجسيمات
 * والتسميات. لولا إدراجه في الترتيب لبقي على قمّة الستايل كلّه فرسم فوق أسماء المدن. */
export const PRECIP_ANIM_LAYER_ID = 'weather-precip-anim';

/** يضبط وضع التراكب ويُعيد ترتيب المكدّس فوراً إن تغيّر. */
function setSparseOverlay(map: MaplibreMap, value: boolean): void {
    if (sparseOverlay === value) return;
    sparseOverlay = value;
    // المرساة تغيّرت، فكل طبقات الطقس القائمة صارت في الموضع الخطأ حتى تُنقل.
    enforceWeatherStackOrder(map);
}

/** أي طبقة تديرها هذه الوحدة (وليست جزءاً من الخريطة الأساسية الحقيقية). */
function isWeatherManagedLayer(id: string): boolean {
    return id === 'weather-particles'
        || id === PRECIP_ANIM_LAYER_ID
        || id === 'weather-heatmap-wind'
        || id === 'weather-heatmap-scalar'
        || id === 'continent-coastline'
        || id === HILLSHADE_LAYER_ID
        || id.endsWith('-sea')            // تمريرات الماء المرافقة لطبقات اللون
        || /^satellite-layer-/.test(id);
}

/**
 * يُعيد id أوّل طبقة **حقيقية** من الخريطة الأساسية (تُستثنى منها طبقات الطقس التي
 * أضفناها نحن) — أوّل طبقة بعد الخلفية مباشرةً (landcover عادةً). هذه "مرساة" ثابتة
 * لا تتغيّر بإضافة/إزالة طبقات الطقس، فتظل كل طبقات الطقس تُدرَج أسفل **كل** تفاصيل
 * الخريطة الأساسية (مياه/غابات/مبانٍ/طرق/حدود/تسميات) بلا اعتماد على ترتيب تركيبها —
 * آخر طبقة طقس تُدرَج تصبح تلقائياً الأعلى بين طبقات الطقس (الأقرب من المرساة).
 * يُعيد undefined إن لم توجد طبقات (فتُضاف طبقة الطقس على القمّة).
 */
function getBaseAnchorId(map: MaplibreMap): string | undefined {
    const layers = map.getStyle()?.layers ?? [];
    return layers.find((l) => l.id !== 'background' && !isWeatherManagedLayer(l.id))?.id;
}

/**
 * مرساة التضاريس في وضع التراكب: **مباشرةً قبل مضلّع الماء**.
 *
 * هذه الحيلة تحلّ مشكلة قديمة بلا تكلفة: طبقة hillshade لا تقبل قناعاً، وبلاطات
 * terrarium تتضمّن أعماق البحار، فرفع التضاريس فوق الماء يكشف ظلّ قاع المحيط. أمّا
 * وضعها **تحت** مضلّع الماء المعتم فيجعل المضلّع نفسه قناعاً مثالياً: الظلّ يقع على
 * اليابسة والخلفية، ثم يطلي الماء فوقه فيمحوه في البحر تماماً. فنكسب ملمس التضاريس
 * على البرّ ونبقي البحر مسطّحاً نظيفاً — وهو بالضبط مظهر Zoom Earth.
 *
 * يعمل فقط حين يكون الماء معتماً (وضع الهطول)؛ لذا لا يُستدعى في الوضع العادي حيث
 * المضلّعات شفّافة ولا تُخفي شيئاً.
 */
function getHillshadeAnchorId(map: MaplibreMap): string | undefined {
    const layers = map.getStyle()?.layers ?? [];
    const water = layers.find((l) => l.type === 'fill'
        && (l.id === 'water' || /(^|[_-])water([_-]|$)|ocean/i.test(l.id)));
    // بلا مضلّع ماء لا قناع، فندفن التضاريس تحت كل المضلّعات بدل كشف قاع المحيط.
    return water?.id ?? getBaseAnchorId(map);
}

/**
 * المرساة التي تُدرَج قبلها طبقات الرادار — أوّل طبقة **خطّية أو نصّية** في النمط.
 *
 * ── لماذا لزمت ────────────────────────────────────────────────────────────────
 * ‏RadarTileLayer يبني مصادره عبر `<Source>/<Layer>` من react-map-gl، وهذه تُلحَق
 * في **قمّة** المكدّس بلا `beforeId` — أي فوق الطرق وحدود المقاطعات والتسميات جميعاً.
 * فكان الصدى يطمس المرجع الأرضي بالضبط حيث يشتدّ، وهو أسوأ موضع لطمسه: المشاهد
 * يسأل «فوق أيّ مدينة هذه البقعة الحمراء؟» فلا يجد المدينة تحتها.
 *
 * والمرجع (‏WeatherFront) يرسم الخطوط **فوق** الألوان، وكذلك RadarScope وWindy.
 *
 * ── ولماذا دالّة مستقلّة لا `getWeatherInsertBeforeId` ────────────────────────
 * تلك تقرأ علَم `sparseOverlay` على مستوى الوحدة، وهو يُضبط من مسار طبقات النسيج
 * الذي لا يمرّ به الرادار أصلاً. فاعتمادُها كان سيربط ترتيب الرادار بحالةٍ يضبطها
 * غيره — عطبٌ يظهر بترتيبٍ مقلوب حسب أيّ طبقة فُتحت قبله.
 */
export function getRadarInsertBeforeId(map: MaplibreMap): string | undefined {
    const layers = map.getStyle()?.layers ?? [];

    /*
     * الرادار يقع **فوق كل مضلّعات الأرض والماء، وتحت كل الخطوط والتسميات**.
     *
     * والشرطان معاً لا أحدهما: الاكتفاء بـ«أوّل طبقة غير-تعبئة» يعطي `waterway` في نمط
     * CARTO، وبعدها مباشرةً يأتي مضلّعا `water` و`water_shadow` — فيقع الصدى تحتهما
     * وتمحوه البحيرات الكبرى. وقِيس ذلك: المرساة كانت الطبقة 6 من 93 و`water` الطبقة 9.
     *
     * فنبحث أوّلاً عن **آخر** مضلّع أرضٍ أو ماء، ثم عن أوّل خطٍّ أو نصٍّ بعده.
     */
    const GROUND = /water|land|park|ocean/i;
    let lastGround = -1;
    layers.forEach((l, i) => {
        if ((l.type === 'fill' || l.type === 'background') && GROUND.test(l.id)) lastGround = i;
    });

    const line = layers.find((l, i) => i > lastGround
        && l.id !== 'background'
        && !l.id.startsWith(IMAGERY_PREFIX)
        && !isWeatherManagedLayer(l.id)
        && l.type !== 'fill' && l.type !== 'fill-extrusion'
        && l.type !== 'background' && l.type !== 'raster' && l.type !== 'hillshade');
    // بلا طبقة خطّية في النمط يبقى الرادار على القمّة — وهو الترتيب السابق نفسه،
    // أي فشلٌ آمن لا مقلوب.
    return line?.id;
}

export function getWeatherInsertBeforeId(map: MaplibreMap): string | undefined {
    const layers = map.getStyle()?.layers ?? [];
    if (sparseOverlay) {
        // وضع التراكب المتفرّق: أوّل طبقة أساس **ليست تعبئة** — أي أوّل طريق/حدّ/تسمية.
        // فيقع حقل الطقس فوق مضلّعات اليابسة والماء (فتظهر قاعدة معتمة تحته) وتحت
        // الخطوط والأسماء (فتبقى مقروءة فوقه) — وهو ترتيب Zoom Earth لطبقة الأمطار.
        const overlay = layers.find((l) => l.id !== 'background' && !isWeatherManagedLayer(l.id)
            && l.type !== 'fill' && l.type !== 'fill-extrusion' && l.type !== 'background'
            && l.type !== 'raster' && l.type !== 'hillshade');
        if (overlay) return overlay.id;
        // بلا طبقة خطّية/نصّية في الستايل نسقط إلى السلوك المعتاد بدل إضافة الطبقة على
        // القمّة فوق كل شيء — الفشل الآمن هنا هو الترتيب القديم لا ترتيب مقلوب.
    }
    return getBaseAnchorId(map);
}

/**
 * الجسيمات يجب أن تبقى دائماً أعلى كل طبقات بيانات الطقس (حرارة/رياح/رادار/أقمار...).
 * كل طبقة بيانات تُدرَج مباشرةً قبل نفس مرساة الخريطة الأساسية (getWeatherInsertBeforeId)،
 * وهذا يدفع أي طبقة جسيمات كانت هناك سابقاً إلى الأسفل. الاعتماد فقط على مستمع
 * 'styledata' لإعادة رفعها لاحقاً قد يتأخّر إطاراً واحداً (يظهر الطبقة الجديدة فوق
 * الجسيمات لحظياً حتى تبديل الجسيمات نفسها). لذا تستدعي كل طبقة بيانات هذه الدالة
 * فوراً وبشكل متزامن بعد addLayer الخاصة بها، فتبقى الجسيمات فوقها دون أي تأخير مرئي.
 */
export function raiseParticlesAboveWeatherLayers(map: MaplibreMap): void {
    enforceWeatherStackOrder(map);
}

/**
 * يفرض الترتيب الداخلي لمجموعة طبقات الطقس (كلّها أسفل مرساة الخريطة الأساسية):
 *
 *   ألوان الطقس ← التضاريس ← تمريرة البحر ← الجسيمات
 *
 * لماذا هذا الترتيب بالذات:
 * - **التضاريس فوق الألوان**: الظلّ يعطي حقل الطقس ملمس الأرض تحته (ترتيب Zoom Earth).
 * - **تمريرة البحر فوق التضاريس**: تعيد طلاء الماء بلونه فتمحو ظلّ قاع البحر — وهو
 *   السبيل الوحيد لقصر التضاريس على اليابسة، إذ لا تقبل طبقة hillshade قناعاً ولا يوجد
 *   نوع طبقة `clip` في MapLibre 5.
 * - **الجسيمات فوق الجميع**: لولا ذلك لغطّتها تمريرة البحر فاختفت خطوط الرياح فوق الماء
 *   تماماً — وهي أوضح ما في المشهد البحري.
 *
 * تُستدعى بعد كل إضافة طبقة طقس (React يُركّب/يفكّك الطبقات حسب اختيار المستخدم، وكل
 * addLayer جديدة تُدرَج عند المرساة فتقفز فوق ما سبقها). نقل كلٍّ بالترتيب إلى ما قبل
 * المرساة يُعيد بناء التسلسل كاملاً بلا افتراضات عن ترتيب الإضافة.
 */
export function enforceWeatherStackOrder(map: MaplibreMap): void {
    const move = (id: string, before: string | undefined) => {
        if (!map.getLayer(id)) return;
        try { map.moveLayer(id, before); } catch { /* تجاهل */ }
    };

    /**
     * ينقل طبقات الحقل اللونية إلى المرساة **مع الحفاظ على ترتيبها النسبي الحالي**.
     *
     * الترتيب بينها (رياح مقابل سلَّمية) يحدّده ترتيب الإضافة في React، ولا تملك هذه
     * الوحدة رأياً فيه؛ فلو نقلناها بترتيب ثابت لقلبنا أيّهما فوق الأخرى عرَضاً. الفرز
     * حسب الموضع الحالي قبل النقل يجعل هذه الدالة تنقل المجموعة كتلةً واحدة لا أكثر.
     */
    const moveFields = (before: string | undefined) => {
        const order = map.getStyle()?.layers ?? [];
        const pos = (id: string) => order.findIndex((l) => l.id === id);
        FIELD_LAYER_IDS.filter((id) => map.getLayer(id))
            .sort((a, b) => pos(a) - pos(b))
            .forEach((id) => move(id, before));
    };

    if (sparseOverlay) {
        // وضع الهطول: القاعدة معتمة والحقل فوقها، والتضاريس بينهما — فوق اليابسة وتحت
        // مضلّع الماء المعتم الذي يعمل قناعاً يمحو ظلّ قاع المحيط (getHillshadeAnchorId).
        move(HILLSHADE_LAYER_ID, getHillshadeAnchorId(map));
        const overlayAnchor = getWeatherInsertBeforeId(map);
        moveFields(overlayAnchor);
        for (const id of [...seaPassIds, PRECIP_ANIM_LAYER_ID, ISOBAR_LAYER_ID, 'weather-particles']) move(id, overlayAnchor);
        return;
    }

    const anchor = getWeatherInsertBeforeId(map);
    // نقل الحقول صراحةً ضروري للعودة من وضع الهطول: هناك رُفعت فوق المضلّعات، ولا شيء
    // يُنزلها ثانيةً لأن الترتيب المعتاد كان يعتمد على موضع الإدراج لا على نقل صريح.
    moveFields(anchor);
    for (const id of [HILLSHADE_LAYER_ID, ...seaPassIds, PRECIP_ANIM_LAYER_ID, ISOBAR_LAYER_ID, 'weather-particles']) {
        move(id, anchor);
    }
}
